/**
 * Fly-in Component
 * Converted from jQuery to native JavaScript with GSAP animations
 * Fixed empty component issue
 */
import { Component, registerComponent } from 'js/component.js';
import { gsap } from 'gsap';

const componentName = 'fly-in';

class FlyIn extends Component {

    /**
     * Component Setup
     */

    showFlyIn() {
        const flyInElement = document.querySelector('#af-flyin');
        if (flyInElement) {
            // Use GSAP for smooth animation instead of CSS transition
            gsap.to(flyInElement, {
                bottom: '0',
                duration: 0.5,
                ease: 'power2.out'
            });
        }
    }

    hideFlyIn() {
        const flyInElement = document.querySelector('#af-flyin');
        if (flyInElement) {
            // Use GSAP for smooth hide animation
            gsap.to(flyInElement, {
                bottom: '-100%',
                duration: 0.3,
                ease: 'power2.inOut',
                onComplete: () => {
                    flyInElement.style.display = 'none';
                }
            });
        }
    }

    setCloseStatus = (id) => {
        if (id) {
            window.sessionStorage.setItem(id, 'true');
        }
    };

    isFlyInClosed = (id) => (
        id ? window.sessionStorage.getItem(id) === 'true' : false
    );

    setCookie(name, value, days) {
        if (!name) return;

        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = `; expires=${date.toUTCString()}`;
        }
        document.cookie = `${name}=${value || ''}${expires}; path=/;secure`;
    }

    getCookieValue = (name) => {
        if (!name) return '';
        return document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop() || '';
    };

    setFlyInCookie = (id) => {
        if (id && this.cookieDays) {
            this.setCookie(id, 'true', this.cookieDays);
        }
    };

    isFlyInDisabled = (id) => (
        this.getCookieValue(id) === 'true' || !this.isFlyInRunning()
    );

    /*
    Determines if the current time is within the fly in on/off time.
     */
    isFlyInRunning = () => {
        if (this.offTime) {
            const currentTime = new Date();
            const onTime = new Date(this.onTime);
            const offTime = new Date(this.offTime);
            return currentTime > onTime && currentTime < offTime;
        }
        return true;
    };

    closeFlyIn = () => {
        this.hideFlyIn();
        this.setCloseStatus(this.id);
    };

    onClickCta = () => {
        this.hideFlyIn();
        this.setFlyInCookie(this.id);
    };

    /**
     * Initializes localStorage data with the key as the ID of the component
     * and the value being the time in 7 days from the current time.
     * @param id
     *   The ID of the fly-in component, used as the key in localStorage.
     */
    initLSData = (id) => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        localStorage.setItem(id, date.toUTCString());
    };

    /**
     * Retrieves the localStorage data for the given key.
     * @param id
     *   The ID of the fly-in component, used as the key in localStorage.
     * @returns {Date|null}
     */
    getLSData = (id) => {
        const lsData = localStorage.getItem(id);
        if (lsData === null) {
            return null;
        }
        return new Date(lsData);
    };

    /**
     * Determines if the fly-in should be shown based on original logic:
     * --> !this.isFlyInDisabled(this.id) && !this.isFlyInClosed(this.id)
     * replaced by this method and validating the 7 date range in localStorage.
     * @param id
     * @returns {boolean}
     */
    shouldShowFlyIn = (id) => {
        const showFlyIn = !this.isFlyInDisabled(id) && !this.isFlyInClosed(id);
        if (!showFlyIn) {
            return showFlyIn;
        }
        const lsData = this.getLSData(id);
        if (lsData === null) {
            // If no localStorage data, initialize it.
            this.initLSData(id);
        } else {
            // Compare current time with localStorage data.
            const currentTime = new Date();
            if (currentTime < lsData) {
                // Within the time range, do not display fly-in.
                return false;
            }
            // Reset localStorage data for the next 7 days.¶
            this.initLSData(id);
        }
        return showFlyIn;
    };

    setup = () => {
        // Check if fly-in component exists and has content
        const flyInConfig = document.querySelector(`.${componentName}`);
        const flyInElement = document.querySelector('#af-flyin');

        // Fix: Check for both config element and actual fly-in element
        if (!flyInConfig || !flyInElement) {
            console.warn('Fly-in component not found or empty');
            return;
        }

        // Fix: Validate that fly-in has actual content (not just empty wrapper)
        const flyInContent = flyInElement.textContent?.trim();
        const flyInImages = flyInElement.querySelectorAll('img');
        const flyInButtons = flyInElement.querySelectorAll('button, .button, a[href]');

        if (!flyInContent && flyInImages.length === 0 && flyInButtons.length === 0) {
            console.warn('Fly-in component is empty - skipping initialization');
            return;
        }

        // Extract data attributes with validation
        this.id = flyInElement.getAttribute('data-flyin-id');
        this.trigger = flyInElement.getAttribute('data-trigger') || 'immediate';
        this.cookieDays = parseInt(flyInElement.getAttribute('data-expiry'), 10) || 7;
        this.onTime = flyInElement.getAttribute('data-on-time');
        this.offTime = flyInElement.getAttribute('data-off-time');

        // Fix: Validate required ID
        if (!this.id) {
            console.warn('Fly-in component missing required data-flyin-id');
            return;
        }

        // Move fly-in to body (same as jQuery appendTo)
        if (flyInConfig.parentNode !== document.body) {
            document.body.appendChild(flyInConfig);
        }

        // Bind close button events
        const closeButtons = document.querySelectorAll(`.${componentName} .-close`);
        closeButtons.forEach((button) => {
            button.addEventListener('click', this.closeFlyIn);
        });

        // Bind CTA button events
        const ctaButtons = document.querySelectorAll(`.${componentName} .${componentName}__cta`);
        ctaButtons.forEach((button) => {
            button.addEventListener('click', this.onClickCta);
        });

        // Check if fly-in should be shown.
        if (this.shouldShowFlyIn(this.id)) {
            if (this.trigger === 'delay') {
                setTimeout(() => {
                    this.showFlyIn();
                }, 5000);
            } else if (this.trigger === 'scroll') {
                // Add scroll listener with throttling to prevent performance issues
                let scrollTimeout;
                const handleScroll = () => {
                    if (scrollTimeout) return;

                    scrollTimeout = setTimeout(() => {
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const bodyHeight = document.body.scrollHeight;

                        if (scrollTop > bodyHeight / 2) {
                            this.showFlyIn();
                            // Remove scroll listener after showing
                            window.removeEventListener('scroll', handleScroll);
                        }
                        scrollTimeout = null;
                    }, 100);
                };
                window.addEventListener('scroll', handleScroll, { passive: true });
            } else {
                // Default: show immediately after a short delay
                setTimeout(() => {
                    this.showFlyIn();
                }, 1000);
            }
        }
    };

    /**
     * Initialize Component
     */
    init() {
        // Listen for fly-in-init event (for dynamic loading)
        window.addEventListener('fly-in-init', () => this.setup());

        // Initialize immediately if DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }
}

registerComponent('.fly-in-config', FlyIn);
